/**
 * Дюймы в сантиметры
 * ====================================
 * TODO В описание TV переменную
 * TODO Список частых значений в дюймах
 * TODO Дробные значения в поиске типо, 5/9 in inches
 */
import { Link, LoaderFunctionArgs, MetaDescriptor, MetaFunction, useLoaderData, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import invariant from "tiny-invariant";
import H2 from "~/components/H2";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Author from "~/components/tool-page/Author";
import Result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";
import { makeTitle } from "~/helpers/pageTitle";
import { createArticleSchema, schemaOrg } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import LengthCalculator, { Unit } from "~/components/LengthCalculator";

const CATEGORY = "length";
const TKEY = "calculators.length.inches_to_cm";
const ROUTE_ENDING = "inches-to-cm";
const LAST_UPDATED = new Date("2024-12-01T00:00:00.000Z");

export const meta: MetaFunction<typeof loader> = function({ data }) {
  invariant(data);

  const meta: MetaDescriptor[] = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createArticleSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      [],
      LAST_UPDATED,
      schemaOrg.authorAlexFedorov
    )
  ];

  if (data.meta.canonical) {
    meta.push({ tagName: "link", rel: "canonical", href: data.meta.canonical });
  }

  return meta;
};

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "pt", "tr", "it", "pl", "ar", "th"]
};

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url) ?? "";
  const url = new URL(request.url);
  const number: number | null = params.number ? Number(params.number) : null;

  const t = await getTranslator(request);

  // Calculate result if number is set
  let error: string | null = null;
  let result: number | null = null;
  let notRoundedResult: number | null = null;
  let pluralizationCount = number ?? 0;

  if (number !== null) {
    if (Number.isNaN(number) || number < 0) {
      error = t(`${TKEY}.error_invalid_number`);
    } else {
      result = inchesToCm(number, true);
      notRoundedResult = inchesToCm(number, false);
    }

    if (number !== Math.floor(number)) {
      if (number < 2) {
        pluralizationCount = 2;
      } else {
        pluralizationCount = Math.floor(number);
      }
    }
  }

  const title = number
    ? t(`${TKEY}.title_for_number`, { count: pluralizationCount, number })
    : t(`${TKEY}.title`)
  ;

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];

  if (number !== null) {
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${number}` });
  }

  let shouldIndex = false;

  const ammoCalibers = [0.17, 0.204, 0.22, 0.223, 0.243, 0.270, 0.30, 0.30, 0.308, 0.338, 0.375, 0.45, 0.50, 0.25, 0.32, 0.357, 0.38, 0.40, 0.44, 0.45, 0.50, 0.410, 0.300, 0.357];

  if (
    // Root page
    number === null
    // .025 increments
    || (number >= 0 && number <= 20 && number * 1000 % 25 === 0)
    // 0.02 increments
    || (number >= 0 && number <= 20 && number * 100 % 2 === 0)
    || (number >= 0 && number <= 100 && number % 1 === 0)
    || ammoCalibers.includes(number)

  ) {
    shouldIndex = true;
  }

  const mainCalculatorURL = url.protocol + "//" + url.host + "/" + locale + "/" + CATEGORY + "/" + ROUTE_ENDING;

  return {
    number,
    result,
    notRoundedResult,
    error,
    meta: {
      locale,
      title,
      description: number !== null
        ? t(`${TKEY}.description_for_number`, { number, tv: "", result: result }) // TODO tv
        : t(`${TKEY}.description`),
      breadcrumbs,
      shouldIndex,
      /**
       * Results page should have a canonical link to the main calculator page.
       * Attempt to glue links juice from results page to the main calculator page,
       * and avoid Google spam content filter
       */
      canonical:  request.url === mainCalculatorURL ? null : mainCalculatorURL,
    }
  };
};

// function generatePermalink() {
//
//   // Analyze the current URL and replace or add the slug accordingly
//   let currentPathname = window.location.pathname;
//   const slugPattern = new RegExp(`/${ROUTE_ENDING}/?(.*)$`);
//
//   if (slugPattern.test(currentPathname)) {
//     // Replace existing slug
//     currentPathname = currentPathname.replace(slugPattern, `/${ROUTE_ENDING}/${number}`);
//   } else {
//     // Add slug to the pathname
//     currentPathname = currentPathname.endsWith("/") ? `/${ROUTE_ENDING}/${number}` : `/${ROUTE_ENDING}/${number}/`;
//   }
//
//   // Redirect to the new path if it changed
//   const newUrl = window.location.origin + currentPathname;
//   if (window.location.href === newUrl) {
//     return;
//   }
//
//   window.location.href = newUrl;
// }

function inchesToCm(number: number, round = true): number {
  const result = number * 2.54;
  return round ? Math.round(number * 2.54 * 10) / 10 : result;
}

function computeTVDimensions(diagonalInches: number) {
  const aspectRatioWidth = 16;
  const aspectRatioHeight = 9;

  const inchesToCm = 2.54;
  diagonalInches = diagonalInches * inchesToCm;

  const hypotenuse = Math.sqrt(aspectRatioWidth ** 2 + aspectRatioHeight ** 2);
  const width = (aspectRatioWidth / hypotenuse) * diagonalInches;
  const height = (aspectRatioHeight / hypotenuse) * diagonalInches;

  return { width: width.toFixed(2), height: height.toFixed(2) };
}


export default function Index() {
  const {
    meta,
    number,
    result,
    notRoundedResult,
    error
  } = useLoaderData<typeof loader>();
  const { t } = useTranslation("translations");
  const locale = getLocale(useLocation().pathname);

  const commonTVDimensions = [32, 40, 43, 50, 55, 65, 75, 77, 80, 82, 86, 90, 98];
  const commonValues = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 19.685, 39.37, 40, 50, 60, 70, 80, 90, 100, 500];


  const formulaSvg = <div className="bg-gray-300 rounded-md p-6 w-[200px] text-black flex justify-center">
    <svg xmlns="http://www.w3.org/2000/svg" width="114.248px" height="13.736px" viewBox="0 -677 6312 759"
         xmlnsXlink="http://www.w3.org/1999/xlink" aria-hidden="true">
      <defs>
        <path id="MJX-10-TEX-I-1D450"
              d="M34 159Q34 268 120 355T306 442Q362 442 394 418T427 355Q427 326 408 306T360 285Q341 285 330 295T319 325T330 359T352 380T366 386H367Q367 388 361 392T340 400T306 404Q276 404 249 390Q228 381 206 359Q162 315 142 235T121 119Q121 73 147 50Q169 26 205 26H209Q321 26 394 111Q403 121 406 121Q410 121 419 112T429 98T420 83T391 55T346 25T282 0T202 -11Q127 -11 81 37T34 159Z"></path>
        <path id="MJX-10-TEX-I-1D45A"
              d="M21 287Q22 293 24 303T36 341T56 388T88 425T132 442T175 435T205 417T221 395T229 376L231 369Q231 367 232 367L243 378Q303 442 384 442Q401 442 415 440T441 433T460 423T475 411T485 398T493 385T497 373T500 364T502 357L510 367Q573 442 659 442Q713 442 746 415T780 336Q780 285 742 178T704 50Q705 36 709 31T724 26Q752 26 776 56T815 138Q818 149 821 151T837 153Q857 153 857 145Q857 144 853 130Q845 101 831 73T785 17T716 -10Q669 -10 648 17T627 73Q627 92 663 193T700 345Q700 404 656 404H651Q565 404 506 303L499 291L466 157Q433 26 428 16Q415 -11 385 -11Q372 -11 364 -4T353 8T350 18Q350 29 384 161L420 307Q423 322 423 345Q423 404 379 404H374Q288 404 229 303L222 291L189 157Q156 26 151 16Q138 -11 108 -11Q95 -11 87 -5T76 7T74 17Q74 30 112 181Q151 335 151 342Q154 357 154 369Q154 405 129 405Q107 405 92 377T69 316T57 280Q55 278 41 278H27Q21 284 21 287Z"></path>
        <path id="MJX-10-TEX-N-3D"
              d="M56 347Q56 360 70 367H707Q722 359 722 347Q722 336 708 328L390 327H72Q56 332 56 347ZM56 153Q56 168 72 173H708Q722 163 722 153Q722 140 707 133H70Q56 140 56 153Z"></path>
        <path id="MJX-10-TEX-I-1D456"
              d="M184 600Q184 624 203 642T247 661Q265 661 277 649T290 619Q290 596 270 577T226 557Q211 557 198 567T184 600ZM21 287Q21 295 30 318T54 369T98 420T158 442Q197 442 223 419T250 357Q250 340 236 301T196 196T154 83Q149 61 149 51Q149 26 166 26Q175 26 185 29T208 43T235 78T260 137Q263 149 265 151T282 153Q302 153 302 143Q302 135 293 112T268 61T223 11T161 -11Q129 -11 102 10T74 74Q74 91 79 106T122 220Q160 321 166 341T173 380Q173 404 156 404H154Q124 404 99 371T61 287Q60 286 59 284T58 281T56 279T53 278T49 278T41 278H27Q21 284 21 287Z"></path>
        <path id="MJX-10-TEX-I-1D45B"
              d="M21 287Q22 293 24 303T36 341T56 388T89 425T135 442Q171 442 195 424T225 390T231 369Q231 367 232 367L243 378Q304 442 382 442Q436 442 469 415T503 336T465 179T427 52Q427 26 444 26Q450 26 453 27Q482 32 505 65T540 145Q542 153 560 153Q580 153 580 145Q580 144 576 130Q568 101 554 73T508 17T439 -10Q392 -10 371 17T350 73Q350 92 386 193T423 345Q423 404 379 404H374Q288 404 229 303L222 291L189 157Q156 26 151 16Q138 -11 108 -11Q95 -11 87 -5T76 7T74 17Q74 30 112 180T152 343Q153 348 153 366Q153 405 129 405Q91 405 66 305Q60 285 60 284Q58 278 41 278H27Q21 284 21 287Z"></path>
        <path id="MJX-10-TEX-N-2217"
              d="M229 286Q216 420 216 436Q216 454 240 464Q241 464 245 464T251 465Q263 464 273 456T283 436Q283 419 277 356T270 286L328 328Q384 369 389 372T399 375Q412 375 423 365T435 338Q435 325 425 315Q420 312 357 282T289 250L355 219L425 184Q434 175 434 161Q434 146 425 136T401 125Q393 125 383 131T328 171L270 213Q283 79 283 63Q283 53 276 44T250 35Q231 35 224 44T216 63Q216 80 222 143T229 213L171 171Q115 130 110 127Q106 124 100 124Q87 124 76 134T64 161Q64 166 64 169T67 175T72 181T81 188T94 195T113 204T138 215T170 230T210 250L74 315Q65 324 65 338Q65 353 74 363T98 374Q106 374 116 368T171 328L229 286Z"></path>
        <path id="MJX-10-TEX-N-32"
              d="M109 429Q82 429 66 447T50 491Q50 562 103 614T235 666Q326 666 387 610T449 465Q449 422 429 383T381 315T301 241Q265 210 201 149L142 93L218 92Q375 92 385 97Q392 99 409 186V189H449V186Q448 183 436 95T421 3V0H50V19V31Q50 38 56 46T86 81Q115 113 136 137Q145 147 170 174T204 211T233 244T261 278T284 308T305 340T320 369T333 401T340 431T343 464Q343 527 309 573T212 619Q179 619 154 602T119 569T109 550Q109 549 114 549Q132 549 151 535T170 489Q170 464 154 447T109 429Z"></path>
        <path id="MJX-10-TEX-N-2E"
              d="M78 60Q78 84 95 102T138 120Q162 120 180 104T199 61Q199 36 182 18T139 0T96 17T78 60Z"></path>
        <path id="MJX-10-TEX-N-35"
              d="M164 157Q164 133 148 117T109 101H102Q148 22 224 22Q294 22 326 82Q345 115 345 210Q345 313 318 349Q292 382 260 382H254Q176 382 136 314Q132 307 129 306T114 304Q97 304 95 310Q93 314 93 485V614Q93 664 98 664Q100 666 102 666Q103 666 123 658T178 642T253 634Q324 634 389 662Q397 666 402 666Q410 666 410 648V635Q328 538 205 538Q174 538 149 544L139 546V374Q158 388 169 396T205 412T256 420Q337 420 393 355T449 201Q449 109 385 44T229 -22Q148 -22 99 32T50 154Q50 178 61 192T84 210T107 214Q132 214 148 197T164 157Z"></path>
        <path id="MJX-10-TEX-N-34"
              d="M462 0Q444 3 333 3Q217 3 199 0H190V46H221Q241 46 248 46T265 48T279 53T286 61Q287 63 287 115V165H28V211L179 442Q332 674 334 675Q336 677 355 677H373L379 671V211H471V165H379V114Q379 73 379 66T385 54Q393 47 442 46H471V0H462ZM293 211V545L74 212L183 211H293Z"></path>
      </defs>
      <g stroke="#000000" fill="#000000" strokeWidth="0" transform="scale(1,-1)">
        <g data-mml-node="math">
          <g data-mml-node="mi">
            <use data-c="1D450" xlinkHref="#MJX-10-TEX-I-1D450"></use>
          </g>
          <g data-mml-node="mi" transform="translate(433,0)">
            <use data-c="1D45A" xlinkHref="#MJX-10-TEX-I-1D45A"></use>
          </g>
          <g data-mml-node="mo" transform="translate(1588.8,0)">
            <use data-c="3D" xlinkHref="#MJX-10-TEX-N-3D"></use>
          </g>
          <g data-mml-node="mi" transform="translate(2644.6,0)">
            <use data-c="1D456" xlinkHref="#MJX-10-TEX-I-1D456"></use>
          </g>
          <g data-mml-node="mi" transform="translate(2989.6,0)">
            <use data-c="1D45B" xlinkHref="#MJX-10-TEX-I-1D45B"></use>
          </g>
          <g data-mml-node="mo" transform="translate(3811.8,0)">
            <use data-c="2217" xlinkHref="#MJX-10-TEX-N-2217"></use>
          </g>
          <g data-mml-node="mn" transform="translate(4534,0)">
            <use data-c="32" xlinkHref="#MJX-10-TEX-N-32"></use>
            <use data-c="2E" xlinkHref="#MJX-10-TEX-N-2E" transform="translate(500,0)"></use>
            <use data-c="35" xlinkHref="#MJX-10-TEX-N-35" transform="translate(778,0)"></use>
            <use data-c="34" xlinkHref="#MJX-10-TEX-N-34" transform="translate(1278,0)"></use>
          </g>
        </g>
      </g>
    </svg>
  </div>;


  return (<>
    <CalculatorLayout>
      <ToolHeaderNew
        name={number ? t(`${TKEY}.question_n`, { count: number }) : meta.title}
        description={meta.description}
      />

      {result !== null && !error ? (
        <Result color="positive">
          <H2 className="first-letter:capitalize">{t("answer")}:</H2>

          <div className="mt-3 mb-8 text-center">
            <strong className="mt-8 text-4xl first-letter:capitalize"> {result} {t(`${TKEY}.unit`)}</strong>
          </div>
          <div className="mt-3 mb-8 text-center">
            {t(`${TKEY}.result`, { from: number, to: result })}
          </div>
          {result !== notRoundedResult ? (
            <div className="mt-3 mb-8 text-center">
              {t(`${TKEY}.result_is_rounded`)} <br />
              {t(`${TKEY}.precise_value`, { result: notRoundedResult })}
            </div>
          ) : null}
        </Result>
      ) : null}

      <ToolForm>
        <LengthCalculator
          from={Unit.inches}
          to={Unit.cm}
          value={number ?? 1}
        />
      </ToolForm>
    </CalculatorLayout>

    <AdditionalContentLayout>
      <AdditionalInfo>
        {number !== null ? <>
          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_how_to_convert_n`, { count: number })}</H2>
            <p>{t(`${TKEY}.info_h_how_to_convert_n_formula`)}</p>
            <div className="flex justify-center mb-4">
              {formulaSvg}
            </div>
            <p>{t(`${TKEY}.info_h_how_to_convert_n_expl`, { count: number })}</p>
            <p className="text-center">{number} in * 2.54 = {result} cm</p>
          </AdditionalInfoSection>
        </> : null}

        {number !== null ? null : <>
          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_how_to_convert`)}</H2>
            <p>{t(`${TKEY}.info_p_how_to_convert`)}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_how_many_cm_in_one_inch`)}</H2>
            <p>{t(`${TKEY}.info_p_how_many_cm_in_one_inch`)}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_how_many_cm_in_one_half_inch`)}</H2>
            <p>{t(`${TKEY}.info_p_how_many_cm_in_one__half_inch`)}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_tv`)}</H2>
            <p>{t(`${TKEY}.info_p_tv`)}</p>
            <table className="w-full table table-fixed text-center border-collapse border border-gray-400 rounded-md">
              <thead>
              <tr>
                <th className="font-bold px-3 py-2 border-b border-r border-gray-400">
                  {t(`${TKEY}.tv_diagonal_inches`)}
                </th>
                <th className="font-bold px-3 py-2 border-b border-r border-gray-400">
                  {t(`${TKEY}.tv_diagonal_cm`)}
                </th>
                <th className="font-bold px-3 py-2 border-b border-r border-gray-400">
                  {t(`${TKEY}.tv_diagonal_width`)}
                </th>
                <th className="font-bold px-3 py-2 border-b border-gray-400">
                  {t(`${TKEY}.tv_diagonal_height`)}
                </th>
              </tr>
              </thead>
              <tbody>
              {commonTVDimensions.map((diagonal) => (
                <tr key={diagonal} className="hover:bg-slate-200 dark:hover:bg-slate-700">
                  <td className="px-3 py-2 border-b  border-r border-gray-400">
                    <Link to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/${diagonal}`}>{diagonal}</Link>
                  </td>
                  <td className="px-3 py-2 border-b  border-r border-gray-400">
                    {inchesToCm(diagonal)}
                  </td>
                  <td
                    className="px-3 py-2 border-b  border-r border-gray-400">{computeTVDimensions(diagonal).width}</td>
                  <td className="px-3 py-2 border-b border-gray-400">{computeTVDimensions(diagonal).height}</td>
                </tr>
              ))}
              </tbody>
            </table>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_pipes`)}</H2>
            <p>{t(`${TKEY}.info_p_pipes`)}</p>
            <table
              className="w-full text-xs md:text-sm table-fixed border-collapse border border-gray-400 text-center">
              <tbody>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">Обозначение</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">Диаметр в дюймах</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">Внешний диаметр в мм</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">Внутренний диаметр в мм</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">Диаметр резьбы в мм</td>
                <td className="px-3 py-2 border-b border-gray-400">Шагов на дюйм</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 1/8"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/0.125`}>1/8</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">9,73</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">8,85</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">8,80</td>
                <td className="px-3 py-2 border-b border-gray-400">28</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 1/4"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/0.25`}>1/4</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">13,16</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">11,89</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">11,80</td>
                <td className="px-3 py-2 border-b border-gray-400">19</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 3/8"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/0.375`}>3/8</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">16,66</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">15,39</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">15,25</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">19</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 1/2"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/0.5`}>1/2</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">20,95</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">19,17</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">19,00</td>
                <td className="px-3 py-2 border-b border-gray-400">14</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 5/8"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/0.625`}>5/8</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">22,91</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">21,13</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">21,00</td>
                <td className="px-3 py-2 border-b border-gray-400">14</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 3/4"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/0.75`}>3/4</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">26,44</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">24,66</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">24,50</td>
                <td className="px-3 py-2 border-b border-gray-400">14</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 7/8"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/0.875`}>7/8</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">30,20</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">28,42</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">28,25</td>
                <td className="px-3 py-2 border-b border-gray-400">14</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 1"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/1`}>1</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">33,25</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">30,93</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">30,75</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 1 1/8"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/1.125`}>1 1/8</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">37,90</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">35,58</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">35,30</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>

              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 1 1/4"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/1.25`}>1 1/4</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">41,91</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">35,59</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">39,25</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>

              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 1 3/8"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/1.375`}>1 3/8</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">44,32</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">42,00</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">41,70</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>

              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 1 1/2"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/1.5`}>1 1/2</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">47,80</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">45,48</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">45,25</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>

              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 2"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/2`}>2</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">59,61</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">57,29</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">57,00</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 2 1/4"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/2.25`}>2 1/4</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">65,71</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">63,39</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">63,10</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 2 1/2"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/2.5`}>2 1/2</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">75,18</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">72,86</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">72,60</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 2 3/4"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/2.75`}>2 3/4</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">81,53</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">79,21</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">78,90</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 3"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/3`}>3</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">87,88</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">85,56</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">85,30</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 3 1/4"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/3.25`}>3 1/4</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">93,98</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">91,66</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">91,50</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 3 1/2"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/3.5`}>3 1/2</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">100,33</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">98,01</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">97,70</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 3 3/4"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/3.75`}>3 3/4</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">106,68</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">104,3</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">104,00</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-r border-gray-400">G 4"</td>
                <td className="px-3 py-2 border-b border-r border-gray-400"><Link
                  to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/4`}>4</Link></td>
                <td className="px-3 py-2 border-b border-r border-gray-400">113,03</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">110,71</td>
                <td className="px-3 py-2 border-b border-r border-gray-400">110,40</td>
                <td className="px-3 py-2 border-b border-gray-400">11</td>
              </tr>
              </tbody>
            </table>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_common_numbers`)}</H2>
            <p>{t(`${TKEY}.info_p_common_numbers`)}</p>
            <table className="w-full table table-fixed text-center border-collapse border border-gray-400 rounded-md">
              <thead>
              <tr>
                <th
                  className="font-bold px-3 py-2 border-b border-r border-gray-400">{t(`${TKEY}.inches`)}</th>
                <th
                  className="font-bold px-3 py-2 border-b border-gray-400">{t(`${TKEY}.unit_full`)}</th>
              </tr>
              </thead>
              <tbody>
              {commonValues.map((inches) => (
                <tr key={inches} className="hover:bg-slate-200 dark:hover:bg-slate-700">
                  <td className="px-3 py-2 border-b border-r border-gray-400">
                   {inches}
                  </td>
                  <td className="px-3 py-2 border-b border-gray-400">{inchesToCm(inches)}</td>
                </tr>
              ))}
              </tbody>
            </table>
          </AdditionalInfoSection>

        </>}

      </AdditionalInfo>

      <Author lastUpdated={LAST_UPDATED} />
    </AdditionalContentLayout>
  </>);
}


